import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { FeatureFlag } from '@metycle/shared';

import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useFeatureFlag } from '@/lib/feature-flags';
import { useCoreValues, type CoreValuesSideState } from '../api/use-core-values';
import { FieldShell } from './adjustment-note-fields';
import { lineTotal, type AdjustmentNoteSubmitValues } from './adjustment-note-schema';
import { formatMoney } from './final-settlement-parts';
import type { useAdjustmentForm } from './use-adjustment-form';

// The "what changed?" flow on the note dialog: the operator declares a price
// or weight change and the note amount is recomputed from the delta against
// the side's current core values (|new − old| × the stationary driver). The
// delta's sign decides credit vs debit, so a change that contradicts the
// chosen button is a hard stop pointing at the other one. The backend reruns
// the same arithmetic — this is the preview, not the enforcement.

type ChangeDriver = 'price' | 'weight';

export interface ComputedChange {
  readonly driver: ChangeDriver;
  readonly oldValue: number;
  readonly newValue: number;
  readonly otherValue: number;
  readonly amount: number;
  readonly impliedKind: 'Credit' | 'Debit';
}

export interface ValueChangeState {
  // False while the chain-PDF feature is off — the section never renders.
  readonly visible: boolean;
  readonly active: boolean;
  readonly currentPrice: number | null;
  readonly currentWeight: number | null;
  readonly priceFinal: boolean;
  readonly weightFinal: boolean;
  readonly computed: ComputedChange | null;
  readonly mismatch: boolean;
  // Save must not proceed: a driver is declared but the change is unusable
  // (missing current values, no new value yet, or the sign contradicts).
  readonly blocked: boolean;
}

function toNumber(value: string | null): number | null {
  if (value === null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function computeChange(
  driver: ChangeDriver,
  newValue: number,
  currentPrice: number | null,
  currentWeight: number | null,
): ComputedChange | null {
  const oldValue = driver === 'price' ? currentPrice : currentWeight;
  const otherValue = driver === 'price' ? currentWeight : currentPrice;
  if (oldValue === null || otherValue === null || newValue === oldValue) return null;
  const delta = Math.abs(newValue - oldValue);
  return {
    driver,
    oldValue,
    newValue,
    otherValue,
    amount: lineTotal(delta, otherValue),
    impliedKind: newValue < oldValue ? 'Credit' : 'Debit',
  };
}

// The synthetic adjustment line a sell-side recomputed note carries, so the
// itemised document still shows what moved. unit_price stays unrounded — the
// wire line_total (quantity × unit_price, 2 dp) then equals the recomputed
// amount instead of drifting through double rounding.
function syntheticLine(computed: ComputedChange): AdjustmentNoteSubmitValues['adjustment_lines'] {
  if (computed.driver === 'price') {
    return [
      {
        type: 'Price adjustment',
        description: `Price change ${computed.oldValue.toString()} → ${computed.newValue.toString()} on ${computed.otherValue.toString()} mt`,
        quantity: computed.otherValue,
        unit: 'mt',
        unit_price: Math.abs(computed.newValue - computed.oldValue),
        line_total: computed.amount,
      },
    ];
  }
  return [
    {
      type: 'Weight adjustment',
      description: `Weight change ${computed.oldValue.toString()} → ${computed.newValue.toString()} mt at ${computed.otherValue.toString()}/mt`,
      quantity: Math.abs(computed.newValue - computed.oldValue),
      unit: 'mt',
      unit_price: computed.otherValue,
      line_total: computed.amount,
    },
  ];
}

interface CurrentValues {
  readonly price: number | null;
  readonly weight: number | null;
  readonly priceFinal: boolean;
  readonly weightFinal: boolean;
}

const NO_CURRENT_VALUES: CurrentValues = {
  price: null,
  weight: null,
  priceFinal: false,
  weightFinal: false,
};

// A driver still on its backfilled value reports no current figure, matching the
// server: those rows hold the deal's contract price on its PLANNED tonnage,
// never an invoiced one. Per driver, so confirming one does not vouch for both.
function currentValuesOf(
  sides: readonly CoreValuesSideState[] | undefined,
  side: 'buy' | 'sell',
): CurrentValues {
  const direction = side === 'buy' ? 'Purchase' : 'Sale';
  const state = sides?.find((s) => s.side === direction);
  if (state === undefined) return NO_CURRENT_VALUES;
  return {
    price: state.price.backfill_only ? null : toNumber(state.price.provisional),
    weight: state.weight_mt.backfill_only ? null : toNumber(state.weight_mt.provisional),
    priceFinal: state.price.is_final,
    weightFinal: state.weight_mt.is_final,
  };
}

function deriveComputed(
  active: boolean,
  driver: 'none' | ChangeDriver,
  newValue: number | undefined,
  current: CurrentValues,
): ComputedChange | null {
  if (!active || driver === 'none') return null;
  if (newValue === undefined || newValue <= 0) return null;
  return computeChange(driver, newValue, current.price, current.weight);
}

// A usable recomputation drives the note's figures; the sell side gets the
// synthetic line (its hidden total field follows the lines on submit).
function useApplyComputed(
  form: ReturnType<typeof useAdjustmentForm>,
  side: 'buy' | 'sell',
  computed: ComputedChange | null,
  mismatch: boolean,
): void {
  const { setValue } = form;
  const sell = side === 'sell';
  useEffect(() => {
    if (computed === null || mismatch) return;
    setValue('total_amount', computed.amount, { shouldValidate: false });
    if (sell) setValue('adjustment_lines', syntheticLine(computed), { shouldValidate: false });
  }, [computed, mismatch, sell, setValue]);
}

export function useNoteValueChange(args: {
  readonly form: ReturnType<typeof useAdjustmentForm>;
  readonly blOrderId: string;
  readonly side: 'buy' | 'sell';
  readonly noteType: 'Credit' | 'Debit';
}): ValueChangeState {
  const visible = useFeatureFlag(FeatureFlag.INVOICING_CHAIN_COMPOSITE_PDF, false);
  const coreValues = useCoreValues(visible ? args.blOrderId : undefined);
  const current = currentValuesOf(coreValues.data?.sides, args.side);
  const driver = useWatch({ control: args.form.control, name: 'change_driver' }) ?? 'none';
  const newValue = useWatch({ control: args.form.control, name: 'change_new_value' });
  const active = visible && driver !== 'none';
  const computed = deriveComputed(active, driver, newValue, current);
  const mismatch = computed !== null && computed.impliedKind !== args.noteType;
  useApplyComputed(args.form, args.side, computed, mismatch);
  return {
    visible,
    active,
    currentPrice: current.price,
    currentWeight: current.weight,
    priceFinal: current.priceFinal,
    weightFinal: current.weightFinal,
    computed,
    mismatch,
    blocked: active && (computed === null || mismatch),
  };
}

function currentLine(state: ValueChangeState): string {
  const price = state.currentPrice === null ? '—' : state.currentPrice.toString();
  const weight = state.currentWeight === null ? '—' : state.currentWeight.toString();
  return `Current: price ${price} · weight ${weight} mt`;
}

function ComputedBanner({
  state,
  noteType,
  currency,
}: Readonly<{ state: ValueChangeState; noteType: 'Credit' | 'Debit'; currency: string }>) {
  const { computed } = state;
  if (computed === null) {
    const missing = state.currentPrice === null || state.currentWeight === null;
    return (
      <p className="text-muted-foreground text-xs">
        {missing
          ? 'No current price/weight on record for this BL side — record core values first.'
          : 'Enter the new value to recompute the note amount.'}
      </p>
    );
  }
  if (state.mismatch) {
    const movement = computed.impliedKind === 'Credit' ? 'decreases' : 'increases';
    return (
      <p className="text-destructive text-sm" role="alert" data-testid="value-change-mismatch">
        This change {movement} the amount owed — create a {computed.impliedKind} Note instead.
      </p>
    );
  }
  return (
    <p className="text-sm" data-testid="value-change-computed">
      Recomputed {noteType.toLowerCase()} note: {formatMoney(computed.amount, currency)}
    </p>
  );
}

export function ValueChangeSection(
  props: Readonly<{
    readonly form: ReturnType<typeof useAdjustmentForm>;
    readonly state: ValueChangeState;
    readonly noteType: 'Credit' | 'Debit';
    readonly disabled: boolean;
  }>,
) {
  const { form, state } = props;
  const currency = useWatch({ control: form.control, name: 'currency' });
  if (!state.visible) return null;
  return (
    <div className="flex flex-col gap-2 rounded-md border p-3">
      <FieldShell id="change_driver" label="What changed?" error={undefined}>
        <Select
          id="change_driver"
          data-testid="value-change-driver"
          disabled={props.disabled}
          {...form.register('change_driver')}
        >
          <option value="none">Nothing recomputed — enter the amount manually</option>
          <option value="price" disabled={state.priceFinal}>
            Price changed{state.priceFinal ? ' (already final)' : ''}
          </option>
          <option value="weight" disabled={state.weightFinal}>
            Weight changed{state.weightFinal ? ' (already final)' : ''}
          </option>
        </Select>
      </FieldShell>
      {state.active ? (
        <>
          <p className="text-muted-foreground text-xs">{currentLine(state)}</p>
          <FieldShell id="change_new_value" label="New value" error={undefined}>
            <Input
              id="change_new_value"
              data-testid="value-change-new-value"
              type="number"
              inputMode="decimal"
              step="any"
              min="0"
              disabled={props.disabled}
              {...form.register('change_new_value', { valueAsNumber: true })}
            />
          </FieldShell>
          <ComputedBanner state={state} noteType={props.noteType} currency={currency} />
        </>
      ) : null}
    </div>
  );
}
